import * as React from 'react';

import {
  enumSalesItemLineType,
  enumSalesLoadLineItemPhase,
  enumUnit,
} from '@rematter/frontend-lib-api-client';
import { uuid } from '@rematter/ui-web';
import { useSetAtom } from 'jotai';

import { ApiBrokeredLoadSalesOrderLineItem } from '../_graphql/querySalesOrderLineItems';
import {
  $brokeredLoadForm,
  $hasUnsavedChanges,
} from '../_state/brokeredLoadDetail.state';
import {
  BrokeredLoadFormLineItem,
  enumBrokeredLoadWeightType,
} from '../types/brokeredLoad';

const lineItemKey = (item: {
  itemId?: string | null;
  purchaseOrderId?: string | null;
}) => `${item.itemId ?? ''}::${item.purchaseOrderId ?? ''}`;

const countByKey = (items: BrokeredLoadFormLineItem[]) =>
  items.reduce((counts, item) => {
    const key = lineItemKey(item);
    counts.set(key, (counts.get(key) ?? 0) + 1);
    return counts;
  }, new Map<string, number>());

const computeUnaddedRequestedItems = (
  requestedItems: BrokeredLoadFormLineItem[],
  shippedItems: BrokeredLoadFormLineItem[],
): BrokeredLoadFormLineItem[] => {
  const shippedCounts = countByKey(shippedItems);
  const remainingCounts = new Map(shippedCounts);

  return requestedItems.filter((item) => {
    const key = lineItemKey(item);
    const remaining = remainingCounts.get(key) ?? 0;
    if (remaining <= 0) return true;
    remainingCounts.set(key, remaining - 1);
    return false;
  });
};

export const createDefaultBrokeredLoadLineItem = (
  data?: Partial<BrokeredLoadFormLineItem>,
): BrokeredLoadFormLineItem => ({
  id: `temp-${uuid()}`,
  type: enumSalesItemLineType.Raw,
  itemId: null,
  purchaseOrderId: null,
  phase: null,
  unitPrice: null,
  estimatedPrice: null,
  pricingUnit: null,
  weights: {
    [enumBrokeredLoadWeightType.Gross]: undefined,
    [enumBrokeredLoadWeightType.Tare]: undefined,
    [enumBrokeredLoadWeightType.Net]: undefined,
  },
  ...data,
});

type UseBrokeredLoadLineItemsManagerOptions = {
  salesOrderLineItems?: ApiBrokeredLoadSalesOrderLineItem[];
  requestedItems?: BrokeredLoadFormLineItem[];
  shippedItems?: BrokeredLoadFormLineItem[];
};

export const useBrokeredLoadLineItemsManager = (
  options?: UseBrokeredLoadLineItemsManagerOptions,
) => {
  const {
    salesOrderLineItems = [],
    requestedItems = [],
    shippedItems = [],
  } = options ?? {};

  const updateForm = useSetAtom($brokeredLoadForm);
  const setHasUnsavedChanges = useSetAtom($hasUnsavedChanges);

  const unaddedRequestedItems = React.useMemo(
    () => computeUnaddedRequestedItems(requestedItems, shippedItems),
    [requestedItems, shippedItems],
  );

  const hasUnaddedSalesOrderItems = React.useMemo(
    () => salesOrderLineItems.length > 0,
    [salesOrderLineItems],
  );

  const hasUnaddedRequestedItems = React.useMemo(
    () => unaddedRequestedItems.length > 0,
    [unaddedRequestedItems],
  );

  const updateItem = React.useCallback(
    (id: string, updates: Partial<BrokeredLoadFormLineItem>) => {
      updateForm((prevForm) => {
        const lineItems = prevForm.lineItems.map((line) => {
          if (line.id === id) {
            return { ...line, ...updates };
          }
          return line;
        });
        return { ...prevForm, lineItems };
      });
      setHasUnsavedChanges(true);
    },
    [updateForm, setHasUnsavedChanges],
  );

  const addItem = React.useCallback(
    (data?: Partial<BrokeredLoadFormLineItem>) => {
      const newItem = createDefaultBrokeredLoadLineItem(data);
      updateForm((prevForm) => ({
        ...prevForm,
        lineItems: [...prevForm.lineItems, newItem],
      }));
      setHasUnsavedChanges(true);
      return newItem.id;
    },
    [updateForm, setHasUnsavedChanges],
  );

  const addItems = React.useCallback(
    (items: Partial<BrokeredLoadFormLineItem>[]) => {
      const newItems = items.map((data) =>
        createDefaultBrokeredLoadLineItem(data),
      );
      updateForm((prevForm) => ({
        ...prevForm,
        lineItems: [...prevForm.lineItems, ...newItems],
      }));
      setHasUnsavedChanges(true);
      return newItems.map((item) => item.id);
    },
    [updateForm, setHasUnsavedChanges],
  );

  const deleteItem = React.useCallback(
    (id: string) => {
      updateForm((prevForm) => {
        const lineItems = prevForm.lineItems.filter((line) => line.id !== id);
        return { ...prevForm, lineItems };
      });
      setHasUnsavedChanges(true);
    },
    [updateForm, setHasUnsavedChanges],
  );

  const addLineItemsFromSalesOrder = React.useCallback(() => {
    if (!salesOrderLineItems.length) return;

    const newItems: Partial<BrokeredLoadFormLineItem>[] =
      salesOrderLineItems.map((soItem) => ({
        itemId: soItem.item.id,
        type: enumSalesItemLineType.Raw,
        phase: enumSalesLoadLineItemPhase.Requested,
        unitPrice: soItem.unitPrice
          ? {
              amount: soItem.unitPrice.amount,
              currencyCode: soItem.unitPrice.currencyCode,
              __typename: 'Money' as const,
            }
          : null,
        pricingUnit: soItem.unit ?? enumUnit.Pounds,
        weights: {
          [enumBrokeredLoadWeightType.Net]: soItem.quantity
            ? {
                value: soItem.quantity,
                unit: soItem.unit ?? enumUnit.Pounds,
                __typename: 'Weight' as const,
              }
            : undefined,
        },
      }));

    addItems(newItems);
  }, [salesOrderLineItems, addItems]);

  const addLineItemsFromRequested = React.useCallback(() => {
    if (!unaddedRequestedItems.length) return;

    const newShippedItems: Partial<BrokeredLoadFormLineItem>[] =
      unaddedRequestedItems.map((item) => {
        const netWeight = item.weights?.[enumBrokeredLoadWeightType.Net];
        const weightUnit = netWeight?.unit ?? enumUnit.Pounds;
        const weightFromNet = netWeight
          ? {
              value: netWeight.value ?? 0,
              unit: weightUnit,
              __typename: 'Weight' as const,
            }
          : undefined;

        return {
          itemId: item.itemId,
          purchaseOrderId: item.purchaseOrderId,
          type: item.type,
          phase: enumSalesLoadLineItemPhase.Shipped,
          unitPrice: item.unitPrice,
          pricingUnit: item.pricingUnit,
          weights: {
            [enumBrokeredLoadWeightType.Gross]: weightFromNet,
            [enumBrokeredLoadWeightType.Tare]: {
              value: 0,
              unit: weightUnit,
              __typename: 'Weight' as const,
            },
            [enumBrokeredLoadWeightType.Net]: weightFromNet,
          },
        };
      });

    addItems(newShippedItems);
  }, [unaddedRequestedItems, addItems]);

  return {
    updateItem,
    addItem,
    addItems,
    deleteItem,
    hasUnaddedSalesOrderItems,
    addLineItemsFromSalesOrder,
    hasUnaddedRequestedItems,
    addLineItemsFromRequested,
  };
};
